import { reactive } from 'cdui-js';
import { fetchConfig } from '../api/common';
import { settings } from '../components/CandlestickChart/settings';

export type H5Theme = 'light' | 'dark';

export const commonState = reactive({
  globalKey: 1,
  encryptKey: '',
  version: '',

  /**
   * 阳线颜色-绿
   */
  bullish: 'rgba(2, 197, 141, 1)',

  /**
   * 阴线颜色-红
   */
  bearish: 'rgba(255, 41, 90, 1)',
  /**
   * 平线颜色-灰
   */
  flat: 'rgba(136, 115, 151, 1)',
  /**
   * 语言
   */
  langauge: 'zh-CN',
  /**
   * 主题
   */
  theme: 'light' as H5Theme,
  /**
   * 红涨绿涨 redup_greendown greenup_reddown
   */
  lineTheme: 'greenup_reddown',
  /**
   * 个股报价模式
   */
  stockMode: 'standard',

  /** /sys/dict/configurations 是否已拉取结束（成功或失败） */
  sysConfigReady: false,
  /**
   * AI 问股开关（configurations.aiStockQaEnabled）
   * 默认关闭；仅后台明确传 true/'true'/1/'1' 时打开
   */
  aiStockQaEnabled: false,
  /**
   * 智能客服开关（configurations.aiCustomerServiceEnabled）
   * 默认关闭；仅后台明确传 true/'true'/1/'1' 时打开
   */
  aiCustomerServiceEnabled: false,
  /**
   * 被禁用交易的市场码（configurations.marketTradeDisabled，字典 MARKET_TRADE_DISABLED），大写
   */
  marketTradeDisabled: [] as string[],

  /** App 下发的安全区（未下发前为 0，由 CSS env / 组件兜底） */
  safeAreaInsets: { top: 0, bottom: 0, left: 0, right: 0 },
  /** true = 已收到 APP_TO_WEB_SafeArea，以 App 值为准（含 bottom: 0） */
  safeAreaFromApp: false,
});

export type AppSafeAreaInsets = {
  top: number;
  bottom: number;
  left: number;
  right: number;
};

const toSafeAreaPx = (n: unknown) => {
  const v = Number(n);
  return Number.isFinite(v) && v >= 0 ? v : 0;
};

/** 解析 App payload（兼容 top/bottom 与 *Inset 命名） */
export const parseSafeAreaPayload = (payload: unknown): AppSafeAreaInsets => {
  const p = (payload ?? {}) as Record<string, unknown>;
  const insets = (p.insets ?? p.safeArea ?? p) as Record<string, unknown>;
  return {
    top: toSafeAreaPx(insets.top ?? insets.topInset),
    bottom: toSafeAreaPx(insets.bottom ?? insets.bottomInset),
    left: toSafeAreaPx(insets.left ?? insets.leftInset),
    right: toSafeAreaPx(insets.right ?? insets.rightInset),
  };
};

/**
 * 写入 App 安全区：优先于 env(safe-area-inset-*)。
 * 未调用前 --footer-height 仍走 CSS env 兜底。
 */
export const applySafeArea = (payload?: unknown): AppSafeAreaInsets => {
  const insets = parseSafeAreaPayload(payload);
  commonState.safeAreaInsets = insets;
  commonState.safeAreaFromApp = true;

  if (typeof document !== 'undefined') {
    const root = document.documentElement;
    root.style.setProperty('--app-safe-area-top', `${insets.top}px`);
    root.style.setProperty('--app-safe-area-bottom', `${insets.bottom}px`);
    root.style.setProperty('--app-safe-area-left', `${insets.left}px`);
    root.style.setProperty('--app-safe-area-right', `${insets.right}px`);
  }

  return insets;
};

/** 读取 CSS 已解析的底部安全区（--footer-height / env），单位 px */
const readCssSafeBottomPx = () => {
  if (typeof document === 'undefined') return 0;
  try {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--footer-height').trim();
    const n = parseFloat(raw);
    if (Number.isFinite(n) && n >= 0) return n;
  } catch {
    /* ignore */
  }
  return 0;
};

/** App WebView 未下发 SafeArea 时：取 env/--footer-height 与平台兜底的较大值 */
export const getCssFallbackSafeBottom = () => {
  const platformFallback = import.meta.env.VITE_PLATFORM !== 'web' ? 34 : 0;
  return Math.max(readCssSafeBottomPx(), platformFallback);
};

/**
 * 有效底部安全距离（px）。
 * - App 已下发 APP_TO_WEB_SafeArea：以 payload.bottom 为准（含 0）
 * - 未下发：CSS env / --footer-height，非 web 再兜底 34px
 */
export const getEffectiveSafeBottom = () =>
  commonState.safeAreaFromApp ? commonState.safeAreaInsets.bottom : getCssFallbackSafeBottom();

/** 兼容后端 bool / string / number；缺省或其它值一律视为关闭 */
export const parseConfigFlag = (value: unknown): boolean =>
  value === true || value === 'true' || value === 1 || value === '1';

/** 解析 marketTradeDisabled：兼容 string[] / 逗号分隔字符串，统一大写去空 */
const parseMarketTradeDisabled = (value: unknown): string[] => {
  const list = Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : [];
  return list.map((item) => (typeof item === 'string' ? item.trim().toUpperCase() : '')).filter(Boolean);
};

/** 等待 encryptKey 的 resolve 队列（configurations 并行拉取时用） */
let encryptKeyWaiters: Array<(key: string) => void> = [];

const flushEncryptKeyWaiters = () => {
  if (!encryptKeyWaiters.length) return;
  // 有 key，或配置已结束（可能无 key）才唤醒；否则继续等
  if (!commonState.encryptKey && !commonState.sysConfigReady) return;
  const key = commonState.encryptKey || '';
  const list = encryptKeyWaiters;
  encryptKeyWaiters = [];
  list.forEach((resolve) => resolve(key));
};

/**
 * 等待 AES 解密密钥就绪。
 * - 已有 encryptKey / 配置已结束：立即返回
 * - 否则短等 configurations（H5 拉取或 App Theme），超时后返回当前值
 * 用于 KYC 回填解密，避免与 config 竞态导致证件号偶发为空。
 */
export const waitForEncryptKey = (timeoutMs = 5000): Promise<string> => {
  if (commonState.encryptKey) return Promise.resolve(commonState.encryptKey);
  if (commonState.sysConfigReady) return Promise.resolve('');

  if (typeof localStorage !== 'undefined' && localStorage.getItem('token')) {
    void loadSysConfigurations().catch(() => {});
  }

  return new Promise((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const finish = (key: string) => {
      if (settled) return;
      settled = true;
      if (timer !== undefined) clearTimeout(timer);
      encryptKeyWaiters = encryptKeyWaiters.filter((w) => w !== onReady);
      resolve(key);
    };

    const onReady = (key: string) => finish(key);
    encryptKeyWaiters.push(onReady);
    timer = setTimeout(() => finish(commonState.encryptKey || ''), timeoutMs);
  });
};

/**
 * 写入系统配置到 commonState。
 * 两个 AI 开关：无字段 / 假值 / 非约定真值 → 关闭；仅约定真值 → 打开。
 */
export const applySysConfigurations = (data: Record<string, unknown> | null | undefined) => {
  const incoming = data?.encryptKey;
  const hasEncryptKey = incoming != null && incoming !== '';
  console.log('[encryptKey][assign]', {
    hasEncryptKey,
    encryptKey: hasEncryptKey ? String(incoming) : '(missing)',
    before: commonState.encryptKey || '(empty)',
  });
  if (hasEncryptKey) {
    commonState.encryptKey = String(incoming);
    console.log('[encryptKey][assign][done]', commonState.encryptKey);
  }
  // 始终按后台值刷新：未传字段时 parseConfigFlag(undefined) → false（保持关闭）
  commonState.aiStockQaEnabled = parseConfigFlag(data?.aiStockQaEnabled);
  commonState.aiCustomerServiceEnabled = parseConfigFlag(data?.aiCustomerServiceEnabled);
  commonState.marketTradeDisabled = parseMarketTradeDisabled(data?.marketTradeDisabled);
  commonState.sysConfigReady = true;
  flushEncryptKeyWaiters();
};

/** 登录后拉取系统配置；无 token 时不请求 */
export const loadSysConfigurations = async () => {
  if (!localStorage.getItem('token')) return;
  try {
    const data = (await fetchConfig()) as unknown as Record<string, unknown>;
    console.log('[encryptKey][loadSysConfigurations]', {
      encryptKey: data?.encryptKey != null && data.encryptKey !== '' ? String(data.encryptKey) : '(missing)',
    });
    applySysConfigurations(data);
  } catch (err) {
    console.log('[encryptKey][loadSysConfigurations][fail]', err);
    commonState.aiStockQaEnabled = false;
    commonState.aiCustomerServiceEnabled = false;
    commonState.sysConfigReady = true;
    flushEncryptKeyWaiters();
  }
};

/** 规范化主题：兼容 App `scheme` / URL `theme` */
export const normalizeH5Theme = (raw?: string | null): H5Theme | null => {
  if (!raw) return null;
  const theme = String(raw).trim().toLowerCase();
  if (theme === 'dark' || theme === 'light') return theme;
  return null;
};

/**
 * 主题检测优先级（与语言一致：URL 保首屏）：
 *   1. URL ?theme= / ?scheme=
 *   2. localStorage 'theme'
 *   3. 默认 light
 */
export const detectH5Theme = (): H5Theme => {
  try {
    if (typeof location !== 'undefined') {
      const params = new URLSearchParams(location.search);
      const fromUrl = normalizeH5Theme(params.get('theme') ?? params.get('scheme'));
      if (fromUrl) return fromUrl;
    }
    const stored = normalizeH5Theme(localStorage.getItem('theme'));
    if (stored) return stored;
  } catch {
    // ignore
  }
  return 'light';
};

/** 写 body/html class + commonState + localStorage */
export const applyTheme = (raw?: string | null): H5Theme => {
  const theme = normalizeH5Theme(raw) ?? detectH5Theme();
  const isDark = theme === 'dark';

  if (typeof document !== 'undefined') {
    const root = document.documentElement;
    const body = document.body;
    root.classList.remove(isDark ? 'light' : 'dark');
    root.classList.add(theme);
    if (body) {
      body.classList.remove(isDark ? 'light' : 'dark');
      body.classList.add(theme);
    }
  }

  commonState.theme = theme;
  try {
    localStorage.setItem('theme', theme);
    (window as any).__DIFI_THEME__ = theme;
  } catch {
    // ignore
  }
  return theme;
};

/** 首屏初始化主题（URL / 本地缓存），无需等 App bridge */
export const initTheme = () => applyTheme(detectH5Theme());

export const initLineTheme = (data?: string) => {
  const value = data || localStorage.getItem('lineTheme') || 'greenup_reddown';
  commonState.bearish = value === 'redup_greendown' ? 'rgba(2, 197, 141, 1)' : 'rgba(255, 41, 90, 1)';
  commonState.bullish = value === 'redup_greendown' ? 'rgba(255, 41, 90, 1)' : 'rgba(2, 197, 141, 1)';

  commonState.lineTheme = value;
  settings.bullish = commonState.bullish;
  settings.bearish = commonState.bearish;
};

// 美股是 >=1 美元报价，3位，  美股 >=1， 3位; >=0.0002 4位，其余6位
// 港股3位
// a股2位
export function getPrecision(market, price?: number) {
  // if (typeof price !== 'number' || isNaN(price)) return 2;
  if (!market) return 2;

  switch (market.toLowerCase()) {
    case 'ash': // A股
      return 2;

    case 'hk': // 港股
      return 3;

    case 'us': // 美股
      return price >= 1 ? 3 : price >= 0.0002 ? 4 : 6;

    default:
      return 2;
  }
}
